import { CommonModule } from '@angular/common';
import { ChangeDetectorRef,
  Component,
  EventEmitter,
  Inject,
  OnDestroy,
  OnInit,
  Output
} from '@angular/core';
import { MatDividerModule } from '@angular/material/divider';
import { MatIconModule } from '@angular/material/icon';
import { MatLegacyButtonModule as MatButtonModule } from '@angular/material/legacy-button';
import {
  MatLegacyDialogModule as MatDialogModule,
  MatLegacyDialogRef as MatDialogRef,
  MAT_LEGACY_DIALOG_DATA as MAT_DIALOG_DATA,
} from '@angular/material/legacy-dialog';
import { MatLegacyProgressSpinnerModule as MatProgressSpinnerModule } from '@angular/material/legacy-progress-spinner';
import {
  MatLegacySnackBar as MatSnackBar,
  MatLegacySnackBarModule as MatSnackBarModule,
} from '@angular/material/legacy-snack-bar';
import { Subject, finalize, takeUntil, Observable } from 'rxjs';
import {
  EBulkCreateInvoiceStatus,
  IBulkCreateInvoiceActionConfig,
  IBulkCreateInvoiceColumn,
} from '../constants/invoice-bulk-create-dialog.constants';
import { trackById, trackByObjectKey } from '../../../../shared/util/track-by.util';

@Component({
  selector: 'lum-invoice-bulk-create-dialog',
  standalone: true,
  imports: [
    CommonModule,
    MatButtonModule,
    MatIconModule,
    MatDividerModule,
    MatProgressSpinnerModule,
    MatSnackBarModule,
    MatDialogModule,
  ],
  templateUrl: './invoice-bulk-create-dialog.component.html',
  styleUrls: ['./invoice-bulk-create-dialog.component.scss'],
})
export class InvoiceBulkCreateDialogComponent implements OnInit, OnDestroy {
  @Output()
  updated = new EventEmitter<boolean>();

  items: any[];
  idKey: any;
  errorKey?: any|null = null;
  columns: IBulkCreateInvoiceColumn<any>[];
  title: string;
  actionLabel: string;
  allProcessed: boolean = false;
  actionFn: (id: number) => Observable<any>;
  actionable: any[] = [];
  unprocessable: any[] = [];
  statuses = new Map<number, EBulkCreateInvoiceStatus>();
  loading = false;
  hasUpdates = false;
  onDestroy$ = new Subject<void>();
  batchStatus = EBulkCreateInvoiceStatus;
  trackById = trackById;
  trackByObjectKey = trackByObjectKey;

  constructor(
    private dialogRef: MatDialogRef<InvoiceBulkCreateDialogComponent>,
    @Inject(MAT_DIALOG_DATA) private data: IBulkCreateInvoiceActionConfig<any>,
    private snackBar: MatSnackBar,
    private cd: ChangeDetectorRef
  ) {
    this.items = data.items;
    this.idKey = data.idKey;
    this.errorKey = data.errorKey;
    this.columns = data.columns;
    this.title = data.title || 'Batch Action';
    this.actionLabel = data.actionLabel || 'Execute';
    this.actionFn = data.actionFn;
  }

  ngOnInit(): void {
    this.items.forEach((item) => {
      const id = Number(item[this.idKey]);
      const errs: string[] =
        this.errorKey && Array.isArray(item[this.errorKey])
          ? (item[this.errorKey] as unknown as string[])
          : [];
      this.statuses.set(id, EBulkCreateInvoiceStatus.PENDING);

      if (errs.length === 0) {
        this.actionable.push(item);
      } else {
        this.unprocessable.push(item);
      }
    });
  }

  ngOnDestroy(): void {
    this.onDestroy$.next();
    this.onDestroy$.complete();
  }

  createAll(): void {
    this.loading = true;
    this.actionable
      .filter(
        (item) => this.statuses.get(Number(item[this.idKey])) === EBulkCreateInvoiceStatus.PENDING
      )
      .forEach((item) => {
        const id = Number(item[this.idKey]);
        this.statuses.set(id, EBulkCreateInvoiceStatus.PROCESSING);

        this.actionFn(id)
          .pipe(
            takeUntil(this.onDestroy$),
            finalize(() => this.checkFinished())
          )
          .subscribe({
            next: () => {
              this.statuses.set(id, EBulkCreateInvoiceStatus.COMPLETED);
              this.hasUpdates = true;
            },
            error: (err) => {
              this.statuses.set(id, EBulkCreateInvoiceStatus.FAILED);
              if (this.errorKey) {
                const arr = (item[this.errorKey] as unknown as string[]) || [];
                arr.push(err?.error?.message || err.error || 'Failed');
                item[this.errorKey] = arr as any;
              }
            },
          });
      });
  }

  private checkFinished(): void {
    const stillProcessing = this.actionable.some(
      (item) => this.statuses.get(Number(String(item[this.idKey]))) === EBulkCreateInvoiceStatus.PROCESSING
    );
    this.loading = stillProcessing;
    if (!stillProcessing) {
      this.allProcessed = true;
      this.snackBar.open(`${this.actionLabel} action completed.`, 'Close', { duration: 3000 });
      this.cd.detectChanges();
    }
  }

  close(): void {
    this.dialogRef.close(this.hasUpdates);
    this.updated.emit(this.hasUpdates);
  }
}
